/**
 * Compliance Calendar route contracts (design v7, §5.3.1).
 *
 * One endpoint() per frontend-consumed (method, path) pair — inventoried from
 * ComplianceCalendar.jsx, TaskDrawer, ReportView and CategoryManager call
 * sites. The worker registers through these; the frontend client invokes the
 * same objects. Same-path method pairs (tasks GET/POST, evidence GET/POST)
 * are contracted separately — the kernel matcher table is method-aware.
 *
 * NOT here (external-only / not frontend-consumed, still served through the
 * module's legacy '/api/compliance-calendar' prefix registration):
 *   GET /api/compliance-calendar/categories  (UI receives categories inside
 *       the tasks payload; template lives in the worker api.ts)
 *   GET /api/compliance-calendar/frameworks  (same — frameworks ride the
 *       tasks payload)
 */

import { defineContracts, endpoint } from '../featureModule'
import { identity } from './compliance_calendar'

export const contracts = defineContracts(identity, {
  // ---- Tasks / occurrences ----------------------------------------------
  /** GET /api/compliance-calendar/tasks?year=YYYY — year view payload. */
  listTasks: endpoint('GET', '/api/compliance-calendar/tasks', { entitlement: 'compliance_calendar' }),
  /** POST /api/compliance-calendar/tasks — create series + initial occurrence. */
  createTask: endpoint('POST', '/api/compliance-calendar/tasks', { entitlement: 'compliance_calendar' }),
  /** PUT /api/compliance-calendar/occurrences/:id — series + occurrence fields. */
  updateOccurrence: endpoint('PUT', '/api/compliance-calendar/occurrences/:id', { entitlement: 'compliance_calendar' }),
  /** DELETE /api/compliance-calendar/tasks/:seriesId — soft-delete series. */
  deleteSeries: endpoint('DELETE', '/api/compliance-calendar/tasks/:seriesId', { entitlement: 'compliance_calendar' }),

  // ---- Categories ---------------------------------------------------------
  createCategory: endpoint('POST', '/api/compliance-calendar/categories', { entitlement: 'compliance_calendar' }),
  updateCategory: endpoint('PUT', '/api/compliance-calendar/categories/:categoryId', { entitlement: 'compliance_calendar' }),

  // ---- Reports ---------------------------------------------------------------
  /** GET /api/compliance-calendar/reports?year&view&framework */
  getReports: endpoint('GET', '/api/compliance-calendar/reports', { entitlement: 'compliance_calendar' }),

  // ---- Suggestions --------------------------------------------------------------
  suggestIsoControls: endpoint('POST', '/api/compliance-calendar/iso-suggestions', { entitlement: 'compliance_calendar' }),
  suggestFrameworks: endpoint('POST', '/api/compliance-calendar/framework-suggestions', { entitlement: 'compliance_calendar' }),

  // ---- Framework links ------------------------------------------------------------
  addFrameworkLink: endpoint('POST', '/api/compliance-calendar/tasks/:seriesId/framework-links', { entitlement: 'compliance_calendar' }),
  removeFrameworkLink: endpoint('DELETE', '/api/compliance-calendar/framework-links/:linkId', { entitlement: 'compliance_calendar' }),

  // ---- Artifact links ---------------------------------------------------------------
  addArtifact: endpoint('POST', '/api/compliance-calendar/tasks/:seriesId/artifacts', { entitlement: 'compliance_calendar' }),
  removeArtifact: endpoint('DELETE', '/api/compliance-calendar/artifacts/:artifactId', { entitlement: 'compliance_calendar' }),
  createRoutineDraft: endpoint('POST', '/api/compliance-calendar/tasks/:seriesId/routine-drafts', { entitlement: 'compliance_calendar' }),

  // ---- Evidence -----------------------------------------------------------------------
  listEvidence: endpoint('GET', '/api/compliance-calendar/occurrences/:id/evidence', { entitlement: 'compliance_calendar' }),
  addEvidence: endpoint('POST', '/api/compliance-calendar/occurrences/:id/evidence', { entitlement: 'compliance_calendar' }),
  removeEvidence: endpoint('DELETE', '/api/compliance-calendar/evidence/:evidenceId', { entitlement: 'compliance_calendar' }),
})
